/**
 * Operations Service (FiadoSA admin)
 * Reconciliation, Jobs, Alerts
 */

import { apiGet, apiPost } from '@/lib/axios';
import { PARTNER_ID } from '@/constants';
import type { ReconciliationDashboard, JobStatus, Alert } from '@/types/ops.types';
import type {
  CollectorReconciliationData,
  RegisterReconciliationRequest,
  ReconciliationRecord,
} from '@/types/collector-reconciliation.types';

// NOTA: /api/ops/* (reconciliation status, jobs, alerts) NO existe como backend en el
// dominio (MAPEO §6/§8) → no hay gemelo v2. Estas features no pegan a nada real hoy.
const BASE = '/api/ops';

export const getReconciliationStatus = () => apiGet<ReconciliationDashboard>(`${BASE}/reconciliation/status`);
export const runReconciliation = (type: string) => apiPost<void>(`${BASE}/reconciliation/run`, { type });
export const getJobsStatus = () => apiGet<JobStatus[]>(`${BASE}/jobs/status`);
export const runJob = (jobName: string) => apiPost<void>(`${BASE}/jobs/run/${jobName}`, {});
export const getActiveAlerts = () => apiGet<Alert[]>(`${BASE}/alerts/active`);
export const acknowledgeAlert = (alertId: string) => apiPost<void>(`${BASE}/alerts/${alertId}/acknowledge`, {});

// ─── Collector Reconciliation ───────────────────────────────────────────────
const RECON = '/credit-worker/v2/backoffice/reconciliation/collector';

export const getCollectorReconciliation = (year: number) =>
  apiGet<CollectorReconciliationData>(`${RECON}?partnerId=${PARTNER_ID}&year=${year}`);

export const registerCollectorReconciliation = (data: RegisterReconciliationRequest) =>
  apiPost<ReconciliationRecord>(`${RECON}/register`, { ...data, partnerId: PARTNER_ID });

export const getCollectorReconciliationHistory = (year: number) =>
  apiGet<ReconciliationRecord[]>(`${RECON}/history?partnerId=${PARTNER_ID}&year=${year}`);

/**
 * Constancia Cesion Parcial — presigned URL por S3 key.
 * El backend (credit-engine) valida que el key coincida con el patron
 * permitido (CREDIT_CONSTANCIA_CESION_PARCIAL bajo bucket de contratos).
 *
 * El frontend obtiene el `key` directamente del row del recon
 * (`ccConstanciaS3Key`) y lo pasa aqui. No requiere lookup en ContractControl.
 */
export interface ConstanciaPdfUrlResponse {
  url: string;
  expires_at: string;
}

export const getConstanciaPdfUrlByKey = (key: string) =>
  apiGet<ConstanciaPdfUrlResponse>(`/credit/v2/backoffice/contracts/pdf-by-key?key=${encodeURIComponent(key)}`);

// ─── FSA Reconciliation (Transfer ↔ Banco) ─────────────────────────────────
const FSA_RECON = '/credit-worker/v2/backoffice/reconciliation/fsa';

export const getFSAReconciliation = (year: number) =>
  apiGet<unknown>(`${FSA_RECON}?partnerId=${PARTNER_ID}&year=${year}`);

export const registerFSAReconciliation = (data: object) =>
  apiPost<ReconciliationRecord>(`${FSA_RECON}/register`, { ...data, partnerId: PARTNER_ID });

export const getFSAReconciliationHistory = (year: number) =>
  apiGet<ReconciliationRecord[]>(`${FSA_RECON}/history?partnerId=${PARTNER_ID}&year=${year}`);

// ─── PCF Reconciliation (UNE-Credito → FSA) ──────────────────────────────────────
const PCF_RECON = '/credit-worker/v2/backoffice/reconciliation/pcf';

export const getPCFReconciliation = (year: number) =>
  apiGet<unknown>(`${PCF_RECON}?partnerId=${PARTNER_ID}&year=${year}`);

export const registerPCFReconciliation = (data: object) =>
  apiPost<ReconciliationRecord>(`${PCF_RECON}/register`, { ...data, partnerId: PARTNER_ID });

export const getPCFReconciliationHistory = (year: number) =>
  apiGet<ReconciliationRecord[]>(`${PCF_RECON}/history?partnerId=${PARTNER_ID}&year=${year}`);

// ─── SIC Report (Circulo de Credito) ────────────────────────────────────────
import type {
    SICReportStatus,
    SICReportHistoryItem,
    SICAttentionItem,
    SICReportDetail,
    SICReportAccount,
    SICRunRequest,
    SICRunResult,
} from '@/types/sic-report.types';

const SIC = '/credit-worker/v2/backoffice/sic-report';

export const getSICReportStatus = (partnerId: string) =>
    apiGet<SICReportStatus | null>(`${SIC}/status?partnerId=${encodeURIComponent(partnerId)}`);

export const getSICReportHistory = (partnerId: string, from?: string, to?: string) => {
    const params = new URLSearchParams({ partnerId });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiGet<SICReportHistoryItem[]>(`${SIC}/history?${params.toString()}`);
};

export const getSICReportAttention = (partnerId: string) =>
    apiGet<SICAttentionItem[]>(`${SIC}/attention?partnerId=${encodeURIComponent(partnerId)}`);

export const getSICReportById = (reportId: string, partnerId: string, runDate: string) =>
    apiGet<SICReportDetail>(
        `${SIC}/${encodeURIComponent(reportId)}?partnerId=${encodeURIComponent(partnerId)}&runDate=${encodeURIComponent(runDate)}`,
    );

export const getSICReportAccount = (reportId: string, creditId: string, partnerId: string, runDate: string) =>
    apiGet<SICReportAccount>(
        `${SIC}/${encodeURIComponent(reportId)}/account/${encodeURIComponent(creditId)}?partnerId=${encodeURIComponent(partnerId)}&runDate=${encodeURIComponent(runDate)}`,
    );

export const runSICReportManual = (body: SICRunRequest) =>
    apiPost<SICRunResult>(`${SIC}/run`, body);
